import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi, categoryApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const POPULAR_ICONS = ['👨‍👩‍👧', '💼', '🧠', '👶', '💑', '🌿', '🎯', '🩺', '🤝', '💭', '🌟', '🧘', '🎓', '🎨', '🛡️', '💊'];

function AdminTherapistCategoriesPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [prescriptionFilter, setPrescriptionFilter] = useState('all'); // 'all', 'required', 'not-required'

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '🌿',
    displayOrder: 0,
    isActive: true,
    prescriptionPadRequired: false,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Confirmation Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, category: null, loading: false });

  // Notification Toast / Alert
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      let res;
      try {
        res = await adminApi.getTherapistCategories({ all: 'true' });
      } catch (adminErr) {
        console.warn('Admin categories endpoint fallback to public endpoint:', adminErr);
        res = await categoryApi.getPublicCategories({ all: 'true' });
      }

      if (res?.success || res?.categories || Array.isArray(res)) {
        const list = res.categories || res.data || (Array.isArray(res) ? res : []);
        setCategories(list);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      showFeedback('error', err.message || 'Failed to load therapist categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Derived Dynamic Real-Time Statistics
  const totalCount = categories.length;
  const activeCount = categories.filter((c) => c.isActive !== false).length;
  const inactiveCount = categories.filter((c) => c.isActive === false).length;
  const prescriptionRequiredCount = categories.filter((c) => Boolean(c.prescriptionPadRequired)).length;
  const prescriptionNotRequiredCount = totalCount - prescriptionRequiredCount;

  // Filtered list for the table display
  const filteredCategories = categories.filter((cat) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (cat.name && cat.name.toLowerCase().includes(q)) ||
      (cat.slug && cat.slug.toLowerCase().includes(q)) ||
      (cat.description && cat.description.toLowerCase().includes(q));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && cat.isActive !== false) ||
      (statusFilter === 'inactive' && cat.isActive === false);

    const matchesPrescription =
      prescriptionFilter === 'all' ||
      (prescriptionFilter === 'required' && Boolean(cat.prescriptionPadRequired)) ||
      (prescriptionFilter === 'not-required' && !cat.prescriptionPadRequired);

    return matchesSearch && matchesStatus && matchesPrescription;
  });

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '🌿',
      displayOrder: categories.length + 1,
      isActive: true,
      prescriptionPadRequired: false,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (cat) => {
    const id = cat._id || cat.id;
    setModalMode('edit');
    setEditingId(id);
    setFormData({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      icon: cat.icon || '🌿',
      displayOrder: cat.displayOrder ?? 0,
      isActive: cat.isActive !== undefined ? cat.isActive : true,
      prescriptionPadRequired: Boolean(cat.prescriptionPadRequired),
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.name.trim()) {
      setFormError('Category name is required');
      return;
    }

    try {
      setFormLoading(true);
      setFormError('');

      const cleanName = formData.name.trim();
      const payload = {
        name: cleanName,
        slug: formData.slug ? formData.slug.trim() : undefined,
        description: formData.description !== undefined ? formData.description.trim() : '',
        icon: formData.icon ? formData.icon.trim() : '🌿',
        displayOrder: Number(formData.displayOrder) || 0,
        isActive: Boolean(formData.isActive),
        prescriptionPadRequired: Boolean(formData.prescriptionPadRequired),
      };

      if (modalMode === 'create') {
        const res = await adminApi.createTherapistCategory(payload);
        showFeedback('success', `Category "${cleanName}" created successfully!`);
        setIsModalOpen(false);
        if (res?.category) {
          setCategories((prev) => [...prev, res.category]);
        }
        fetchCategories();
      } else {
        const catId = editingId;
        if (!catId) {
          throw new Error('Category ID is missing for edit operation');
        }

        // Optimistic state update so cards and table update instantly
        setCategories((prev) =>
          prev.map((c) =>
            (c._id === catId || c.id === catId)
              ? { ...c, ...payload }
              : c
          )
        );

        const res = await adminApi.updateTherapistCategory(catId, payload);
        showFeedback('success', `Category "${cleanName}" updated successfully!`);
        setIsModalOpen(false);
        if (res?.category) {
          setCategories((prev) =>
            prev.map((c) =>
              (c._id === catId || c.id === catId) ? res.category : c
            )
          );
        }
        fetchCategories();
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setFormError(err.message || 'Failed to save category');
      fetchCategories(); // Revert on failure
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (cat) => {
    const catId = cat._id || cat.id;
    const nextStatus = !(cat.isActive !== false);

    // Optimistic instant state update
    setCategories((prev) =>
      prev.map((c) => ((c._id === catId || c.id === catId) ? { ...c, isActive: nextStatus } : c))
    );

    try {
      const res = await adminApi.toggleTherapistCategory(catId);
      const finalStatus = res?.category ? res.category.isActive : nextStatus;
      showFeedback('success', `Category "${cat.name}" is now ${finalStatus ? 'Active' : 'Inactive'}`);
      if (res?.category) {
        setCategories((prev) =>
          prev.map((c) => ((c._id === catId || c.id === catId) ? res.category : c))
        );
      }
    } catch (err) {
      console.error('Error toggling category status:', err);
      showFeedback('error', err.message || 'Failed to toggle status');
      fetchCategories(); // Revert on failure
    }
  };

  const handleTogglePrescription = async (cat) => {
    const catId = cat._id || cat.id;
    const nextReq = !Boolean(cat.prescriptionPadRequired);

    // Optimistic instant state update
    setCategories((prev) =>
      prev.map((c) => ((c._id === catId || c.id === catId) ? { ...c, prescriptionPadRequired: nextReq } : c))
    );

    try {
      let res;
      if (adminApi.toggleTherapistCategoryPrescription) {
        res = await adminApi.toggleTherapistCategoryPrescription(catId);
      } else {
        res = await adminApi.updateTherapistCategory(catId, {
          prescriptionPadRequired: nextReq,
        });
      }

      const finalReq = res?.category ? Boolean(res.category.prescriptionPadRequired) : nextReq;
      showFeedback(
        'success',
        `Prescription Pad is now ${finalReq ? 'Required' : 'Not Required'} for "${cat.name}"`
      );
      if (res?.category) {
        setCategories((prev) =>
          prev.map((c) => ((c._id === catId || c.id === catId) ? res.category : c))
        );
      }
    } catch (err) {
      console.error('Error toggling prescription requirement:', err);
      showFeedback('error', err.message || 'Failed to update prescription requirement');
      fetchCategories(); // Revert on failure
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.category) return;
    const catId = deleteModal.category._id || deleteModal.category.id;
    const catName = deleteModal.category.name;

    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      // Optimistic delete
      setCategories((prev) => prev.filter((c) => (c._id !== catId && c.id !== catId)));

      const res = await adminApi.deleteTherapistCategory(catId);
      showFeedback('success', `Category "${catName}" deleted successfully`);
      setDeleteModal({ isOpen: false, category: null, loading: false });
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      showFeedback('error', err.message || 'Failed to delete category');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
      fetchCategories(); // Revert on failure
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <main className="admin-main-wrapper admin-custom-scrollbar">
        <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '32px 36px 80px 36px' }}>
          
          {/* Breadcrumb Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b', marginBottom: '16px', fontWeight: 500 }}>
            <span>Admin</span>
            <span>/</span>
            <span>Practitioners</span>
            <span>/</span>
            <span style={{ color: '#7c3aed', fontWeight: 600 }}>Categories & Specializations</span>
          </div>

          {/* Top Header Banner */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)',
              borderRadius: '20px',
              padding: '32px 36px',
              color: '#ffffff',
              marginBottom: '28px',
              boxShadow: '0 12px 32px rgba(49, 46, 129, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background decorative glow */}
            <div
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-40px',
                width: '180px',
                height: '180px',
                background: 'radial-gradient(circle, rgba(124, 58, 237, 0.4) 0%, rgba(255,255,255,0) 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }}
            />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
                <span
                  style={{
                    fontSize: '28px',
                    width: '54px',
                    height: '54px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  🏷️
                </span>
                <div>
                  <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, letterSpacing: '-0.5px', color: '#ffffff' }}>
                    Therapist Categories & Taxonomy
                  </h1>
                  <span style={{ fontSize: '12px', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Clinical Specializations
                  </span>
                </div>
              </div>
              <p style={{ margin: '8px 0 0 0', color: 'rgba(255, 255, 255, 0.85)', fontSize: '14.5px', maxWidth: '640px', lineHeight: 1.5 }}>
                Configure practitioner clinical domains, display order, active status, and enforce medical prescription pad requirement policies during onboarding.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 1 }}>
              <button
                type="button"
                onClick={handleOpenCreateModal}
                style={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '13px 24px',
                  fontWeight: 700,
                  fontSize: '14.5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 6px 20px rgba(168, 85, 247, 0.35)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 85, 247, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(168, 85, 247, 0.35)';
                }}
              >
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>➕</span> Add New Category
              </button>
            </div>
          </div>

          {/* Toast Notification */}
          {feedback.message && (
            <div
              style={{
                position: 'fixed',
                top: '24px',
                right: '24px',
                zIndex: 99999,
                padding: '14px 22px',
                borderRadius: '14px',
                fontWeight: 700,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
                background: feedback.type === 'success' ? '#065f46' : '#991b1b',
                color: '#ffffff',
                border: feedback.type === 'success' ? '1px solid #10b981' : '1px solid #ef4444',
                animation: 'adminSlideDown 0.2s ease-out',
              }}
            >
              <span style={{ fontSize: '18px' }}>{feedback.type === 'success' ? '✅' : '⚠️'}</span>
              <span>{feedback.message}</span>
              <button
                onClick={() => setFeedback({ type: '', message: '' })}
                style={{ background: 'none', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '16px', padding: 0, marginLeft: '8px' }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Quick Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '28px' }}>
            {/* Metric 1: Total */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#f1f5f9',
                  color: '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                🏷️
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Categories
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>
                  {totalCount}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Configured clinical domains
                </div>
              </div>
            </div>

            {/* Metric 2: Active */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#ecfdf5',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                🟢
              </div>
              <div>
                <div style={{ color: '#059669', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Active Categories
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#059669', marginTop: '2px' }}>
                  {activeCount}
                </div>
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '2px', fontWeight: 600 }}>
                  Live in client booking filter
                </div>
              </div>
            </div>

            {/* Metric 3: Prescription Required */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1.5px solid #ede9fe',
                boxShadow: '0 4px 14px rgba(124, 58, 237, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#f5f3ff',
                  color: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                📋
              </div>
              <div>
                <div style={{ color: '#7c3aed', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Prescription Required
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#7c3aed', marginTop: '2px' }}>
                  {prescriptionRequiredCount}
                </div>
                <div style={{ fontSize: '12px', color: '#6d28d9', marginTop: '2px' }}>
                  Mandatory Rx Pad policy
                </div>
              </div>
            </div>

            {/* Metric 4: Standard / No Rx Required */}
            <div
              style={{
                background: '#ffffff',
                padding: '22px 24px',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: '#f8fafc',
                  color: '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                🚫
              </div>
              <div>
                <div style={{ color: '#475569', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Standard / Non-Rx
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#334155', marginTop: '2px' }}>
                  {prescriptionNotRequiredCount}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Psychotherapy & Counseling
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filter Toolbar */}
          <div
            style={{
              background: '#ffffff',
              padding: '18px 24px',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            {/* Search Box */}
            <div style={{ position: 'relative', flex: '1', minWidth: '280px', maxWidth: '440px' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '16px' }}>
                🔍
              </span>
              <input
                type="text"
                placeholder="Search by category name, slug, or scope..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '11px 16px 11px 40px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#f8fafc',
                  color: '#1e293b',
                  boxSizing: 'border-box',
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Filter Pills */}
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Status Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f8fafc', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', paddingLeft: '8px', paddingRight: '4px' }}>
                  Status:
                </span>
                {['all', 'active', 'inactive'].map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setStatusFilter(status)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: statusFilter === status ? '#7c3aed' : 'transparent',
                      color: statusFilter === status ? '#ffffff' : '#64748b',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {status === 'all' ? 'All' : status}
                  </button>
                ))}
              </div>

              {/* Prescription Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f8fafc', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', paddingLeft: '8px', paddingRight: '4px' }}>
                  Prescription:
                </span>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'required', label: '📝 Required' },
                  { id: 'not-required', label: '🚫 Optional' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setPrescriptionFilter(item.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: prescriptionFilter === item.id ? '#4338ca' : 'transparent',
                      color: prescriptionFilter === item.id ? '#ffffff' : '#64748b',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Clear button */}
              {(searchQuery || statusFilter !== 'all' || prescriptionFilter !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setPrescriptionFilter('all');
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: '1px dashed #ef4444',
                    background: '#fef2f2',
                    color: '#ef4444',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Results count label */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', padding: '0 4px' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Showing <strong>{filteredCategories.length}</strong> of <strong>{categories.length}</strong> categories
            </span>
          </div>

          {/* Categories Table Card */}
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              overflow: 'hidden',
            }}
          >
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                <div style={{ fontWeight: 600, fontSize: '15px' }}>Loading therapist categories...</div>
              </div>
            ) : filteredCategories.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '16px 22px' }}>Category Name</th>
                      <th style={{ padding: '16px 20px' }}>URL Identifier</th>
                      <th style={{ padding: '16px 20px' }}>Scope & Clinical Details</th>
                      <th style={{ padding: '16px 20px', textAlign: 'center' }}>Prescription Pad Policy</th>
                      <th style={{ padding: '16px 20px', textAlign: 'center' }}>Order</th>
                      <th style={{ padding: '16px 20px', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '16px 22px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.map((cat, idx) => {
                      const catId = cat._id || cat.id;
                      const isRxReq = Boolean(cat.prescriptionPadRequired);
                      const isCatActive = cat.isActive !== false;

                      return (
                        <tr
                          key={catId || idx}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background-color 0.15s ease',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fbfcfe')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          {/* Name & Icon */}
                          <td style={{ padding: '16px 22px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <span
                                style={{
                                  fontSize: '22px',
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '12px',
                                  background: '#f5f3ff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1.5px solid #ede9fe',
                                  flexShrink: 0,
                                }}
                              >
                                {cat.icon || '🌿'}
                              </span>
                              <div>
                                <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14.5px' }}>
                                  {cat.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                                  ID: {catId ? catId.slice(-6) : '—'}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Slug */}
                          <td style={{ padding: '16px 20px' }}>
                            <code
                              style={{
                                background: '#f1f5f9',
                                color: '#4338ca',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '12.5px',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                              }}
                            >
                              {cat.slug || cat.name?.toLowerCase().replace(/\s+/g, '-')}
                            </code>
                          </td>

                          {/* Description */}
                          <td style={{ padding: '16px 20px', maxWidth: '300px' }}>
                            <span
                              style={{
                                fontSize: '13px',
                                color: '#64748b',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                              }}
                              title={cat.description}
                            >
                              {cat.description || 'No description provided'}
                            </span>
                          </td>

                          {/* Prescription Pad Requirement Badge & Toggle */}
                          <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleTogglePrescription(cat)}
                              title="Click to toggle Prescription Pad Requirement for practitioners"
                              style={{
                                padding: '6px 14px',
                                borderRadius: '20px',
                                border: isRxReq ? '1.5px solid #c4b5fd' : '1.5px solid #e2e8f0',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                background: isRxReq ? '#f5f3ff' : '#f8fafc',
                                color: isRxReq ? '#6d28d9' : '#64748b',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: isRxReq ? '0 2px 6px rgba(124, 58, 237, 0.12)' : 'none',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <span>{isRxReq ? '📝' : '🚫'}</span>
                              <span>{isRxReq ? 'Rx Pad Required' : 'Not Required'}</span>
                            </button>
                          </td>

                          {/* Display Order */}
                          <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <span
                              style={{
                                fontWeight: 700,
                                color: '#334155',
                                fontSize: '13px',
                                background: '#f1f5f9',
                                padding: '4px 10px',
                                borderRadius: '8px',
                              }}
                            >
                              {cat.displayOrder ?? 0}
                            </span>
                          </td>

                          {/* Active Status */}
                          <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(cat)}
                              title="Click to toggle category status"
                              style={{
                                padding: '6px 14px',
                                borderRadius: '20px',
                                border: isCatActive ? '1px solid #a7f3d0' : '1px solid #cbd5e1',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                background: isCatActive ? '#ecfdf5' : '#f1f5f9',
                                color: isCatActive ? '#065f46' : '#64748b',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <span
                                style={{
                                  width: '7px',
                                  height: '7px',
                                  borderRadius: '50%',
                                  background: isCatActive ? '#10b981' : '#94a3b8',
                                }}
                              />
                              {isCatActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '16px 22px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditModal(cat)}
                                style={{
                                  padding: '7px 14px',
                                  borderRadius: '8px',
                                  border: '1px solid #cbd5e1',
                                  background: '#ffffff',
                                  color: '#334155',
                                  fontSize: '12.5px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#7c3aed';
                                  e.currentTarget.style.color = '#7c3aed';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.color = '#334155';
                                }}
                              >
                                <span>✏️</span> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteModal({ isOpen: true, category: cat, loading: false })}
                                style={{
                                  padding: '7px 12px',
                                  borderRadius: '8px',
                                  border: '1px solid #fecaca',
                                  background: '#fff5f5',
                                  color: '#dc2626',
                                  fontSize: '12.5px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fff5f5';
                                }}
                              >
                                <span>🗑️</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '14px' }}>🏷️</div>
                <h4 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '6px', fontSize: '18px' }}>
                  No categories found
                </h4>
                <p style={{ color: '#64748b', margin: '0 auto 20px auto', maxWidth: '420px', fontSize: '14px' }}>
                  {searchQuery || statusFilter !== 'all' || prescriptionFilter !== 'all'
                    ? 'No categories match the active filters. Try adjusting your search query or filters.'
                    : 'No therapist categories configured yet. Create the first one below.'}
                </p>
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  style={{
                    padding: '11px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(124, 58, 237, 0.25)',
                  }}
                >
                  ➕ Create First Category
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ========================================================================= */}
      {/* CREATE / EDIT CATEGORY MODAL */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            animation: 'adminFadeIn 0.2s ease-out',
          }}
          onClick={() => !formLoading && setIsModalOpen(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '620px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              animation: 'adminScaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                  {modalMode === 'create' ? '➕ Add Therapist Category' : '✏️ Edit Therapist Category'}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.88rem', color: '#64748b' }}>
                  {modalMode === 'create'
                    ? 'Define clinical domain and set prescription pad requirement for practitioners'
                    : `Updating category: ${formData.name}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '50%',
                  width: '34px',
                  height: '34px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} style={{ padding: '24px 28px' }}>
              {formError && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '13px', marginBottom: '18px', fontWeight: 600 }}>
                  ⚠️ {formError}
                </div>
              )}

              {/* Name Field */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Category Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Psychiatry & Neuropsych, Family Therapist, Child Psychology"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Slug Field */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Slug (URL Identifier)
                </label>
                <input
                  type="text"
                  placeholder="e.g. psychiatry-neuropsych (auto-generated if empty)"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    outline: 'none',
                    background: '#f8fafc',
                    boxSizing: 'border-box',
                    fontFamily: 'monospace',
                  }}
                />
              </div>

              {/* Icon Picker */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Category Icon / Emoji
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    style={{
                      width: '60px',
                      textAlign: 'center',
                      fontSize: '22px',
                      padding: '8px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                    }}
                  />
                  <span style={{ fontSize: '12.5px', color: '#64748b' }}>Select from preset icons below or enter custom emoji</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {POPULAR_ICONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: formData.icon === icon ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                        background: formData.icon === icon ? '#f5f3ff' : '#ffffff',
                        fontSize: '18px',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prescription Pad Requirement Configuration */}
              <div style={{ marginBottom: '20px', background: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1.5px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '13.5px', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>
                  📋 Prescription Pad Requirement Policy
                </label>
                <p style={{ margin: '0 0 14px 0', fontSize: '12.5px', color: '#64748b' }}>
                  Specify whether therapists registered under this specialization domain must submit an official prescription pad during onboarding.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {/* Option 1: Required */}
                  <div
                    onClick={() => setFormData({ ...formData, prescriptionPadRequired: true })}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      border: formData.prescriptionPadRequired ? '2px solid #7c3aed' : '1.5px solid #cbd5e1',
                      background: formData.prescriptionPadRequired ? '#f5f3ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      boxShadow: formData.prescriptionPadRequired ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="prescriptionPadOption"
                      checked={formData.prescriptionPadRequired === true}
                      onChange={() => setFormData({ ...formData, prescriptionPadRequired: true })}
                      style={{ marginTop: '3px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13.5px', color: formData.prescriptionPadRequired ? '#6d28d9' : '#1e293b' }}>
                        📝 Required
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px', lineHeight: 1.4 }}>
                        Mandatory for medical/psychiatric practitioners who issue medication prescriptions.
                      </div>
                    </div>
                  </div>

                  {/* Option 2: Not Required */}
                  <div
                    onClick={() => setFormData({ ...formData, prescriptionPadRequired: false })}
                    style={{
                      padding: '14px',
                      borderRadius: '12px',
                      border: !formData.prescriptionPadRequired ? '2px solid #4338ca' : '1.5px solid #cbd5e1',
                      background: !formData.prescriptionPadRequired ? '#eef2ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      boxShadow: !formData.prescriptionPadRequired ? '0 4px 12px rgba(67, 56, 202, 0.15)' : 'none',
                    }}
                  >
                    <input
                      type="radio"
                      name="prescriptionPadOption"
                      checked={formData.prescriptionPadRequired === false}
                      onChange={() => setFormData({ ...formData, prescriptionPadRequired: false })}
                      style={{ marginTop: '3px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13.5px', color: !formData.prescriptionPadRequired ? '#3730a3' : '#1e293b' }}>
                        🚫 Not Required
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px', lineHeight: 1.4 }}>
                        For counseling, talk therapy, coaching, and non-prescribing psychology domains.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Field */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Description (Scope of Care)
                </label>
                <textarea
                  rows="3"
                  placeholder="Provide an overview of clinical scope, targeted issues, and treatment methodologies..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Display Order & Active Checkbox */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '13.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13.5px', fontWeight: 600, color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Active / Visible in Directory
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={formLoading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                  }}
                >
                  {formLoading ? 'Saving...' : modalMode === 'create' ? 'Create Category' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ========================================================================= */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(8px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            animation: 'adminFadeIn 0.2s ease-out',
          }}
          onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, category: null, loading: false })}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '440px',
              padding: '28px',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              animation: 'adminScaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
              Delete Category?
            </h3>
            <p style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.5, marginBottom: '24px' }}>
              Are you sure you want to delete category <strong>"{deleteModal.category?.name}"</strong>? This will permanently remove this specialization category.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, category: null, loading: false })}
                disabled={deleteModal.loading}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteModal.loading}
                style={{
                  padding: '10px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#ffffff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
                }}
              >
                {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTherapistCategoriesPage;
